import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axiosInstance from './axios';
import {
  CustomerHistoryResponseType,
  GetAllQuotationRequestT,
  quotaionEditBody,
  QuotationByIdResponse,
  QuotationResponse,
} from '../types/quatationType';
import { format } from 'date-fns';

export interface GetAllQuotationParams {
  pageNo: number | null;
  pageSize: number;
  quotationNumber: string;
  quotationAmountFrom: number | null;
  quotationAmountTo: number | null;
  paidAmountFrom: number | null;
  paidAmountTo: number | null;
  status?: boolean;
  fromDate: string;
  toDate: string;
  customerName: string;
  customerEmail?: string;
  salesStatus: string;
  isDraft: boolean;
}

export const useGetAllQuotation = (postData: GetAllQuotationParams) => {
  const queryParamsObj: Record<string, string> = {
    pageNo: (postData.pageNo ?? '')?.toString(),
    pageSize: postData.pageSize?.toString(),
    quotationNumber: postData.quotationNumber,
    quotationAmountFrom:
      postData.quotationAmountFrom !== null
        ? postData.quotationAmountFrom?.toString()
        : '',
    quotationAmountTo:
      postData.quotationAmountTo !== null
        ? postData.quotationAmountTo?.toString()
        : '',
    paidAmountFrom:
      postData.paidAmountFrom !== null
        ? postData.paidAmountFrom?.toString()
        : '',
    paidAmountTo:
      postData.paidAmountTo !== null ? postData.paidAmountTo?.toString() : '',
    fromDate:
      postData.fromDate && format(new Date(postData.fromDate), 'yyyy-MM-dd'),
    toDate: postData.toDate && format(new Date(postData.toDate), 'yyyy-MM-dd'),
    customerName: postData.customerName,
    salesStatus: postData.salesStatus,
  };

  if (postData.status !== undefined) {
    queryParamsObj.status = postData.status.toString();
  }
  if (postData.customerEmail !== undefined) {
    queryParamsObj.customerEmail = postData.customerEmail;
  }
  const queryParams = new URLSearchParams(queryParamsObj).toString();

  return useQuery({
    queryKey: ['useGetAllQuotation', postData],
    queryFn: async () => {
      const { data } = await axiosInstance.post(
        `sales/quotation/all`,
        postData
      );
      return data as QuotationResponse;
    },
  });
};

export const useAddQuotation = (options = {}) => {
  return useMutation({
    mutationKey: ['useAddQuotation'],
    mutationFn: async (postData: {
      customerId: string;
      reqNumber?: string;
      quotationNumber?: string;
      isDraft?: boolean;
      trn?: string;
      quotationProductRequests: Array<{
        productId: number;
        quantity: number;
        unitPrice: number;
        discountAmount: number;
        isDiscountPercentage: boolean;
        taxPercentage: number;
        unit: string;
        conversionToBaseUnit: number;
        baseQuantity: number;
      }>;
    }) => {
      const { data } = await axiosInstance.post(
        'sales/quotation/save',
        postData
      );
      return data;
    },
    ...options,
  });
};

export const useQuotationPaid = (options = {}) => {
  return useMutation({
    mutationKey: ['useQuotationPaid'],
    mutationFn: async (postData: {
      quotationId: string;
      paidAmount: number;
      tRNNumber?: string;
    }) => {
      const { data } = await axiosInstance.post(
        `sales/quotation/paid?quotationId=${postData.quotationId}&paidAmount=${postData.paidAmount}&tRNNumber=${postData.tRNNumber}`
      );
      return data;
    },
    ...options,
  });
};

// export const useInfiniteGetAllQuotation = (postData: GetAllQuotationInfiniteParams) => {
//   return useInfiniteQuery({
//     queryKey: ['useInfiniteGetAllQuotation', postData],
//     queryFn: async ({ pageParam = 0 }) => {
//       const params = new URLSearchParams({
//         pageNo: pageParam.toString(),
//         pageSize: postData.pageSize.toString(),
//         ...(postData.customerId ? { customerId: postData.customerId.toString() } : {}),
//       }).toString();

//       const { data } = await axiosInstance.post(
//         `/sales/quotation/all?${params}`,
//         postData
//       );
//       return data as QuotationResponse & { hasNextPage?: boolean };
//     },
//     getNextPageParam: (lastPage, allPages) => {
//       const nextPage = lastPage.hasNextPage ? allPages.length : undefined;
//       return nextPage;
//     },
//     initialPageParam: 0,
//   });
// };

// export const useInfiniteGetAllQuotation = (
//   postData: GetAllQuotationRequestT
// ) => {
//   return useInfiniteQuery({
//     queryKey: ['useInfiniteGetAllQuotation', postData],
//     queryFn: async ({ pageParam = 0 }) => {
//       const { data } = await axiosInstance.post(`/sales/quotation/all`, {
//         ...postData,
//         pageNo: pageParam,
//       });
//       return data as QuotationResponse & { hasNextPage?: boolean };
//     },
//     getNextPageParam: (lastPage, allPages) => {
//       const nextPage = lastPage.hasNextPage ? allPages.length : undefined;
//       return nextPage;
//     },
//     initialPageParam: 0,
//   });
// };

export const useInfiniteGetAllQuotation = (
  postData: GetAllQuotationRequestT
) => {
  return useInfiniteQuery({
    queryKey: ['useInfiniteGetAllQuotation', postData],
    queryFn: async ({ pageParam = 0 }) => {
      const params = new URLSearchParams({
        pageNo: pageParam.toString(),
        pageSize: postData.pageSize.toString(),
        ...(postData.customerId
          ? { customerId: postData.customerId.toString() }
          : {}),
      }).toString();

      const { data } = await axiosInstance.post(
        `/sales/quotation/all`,
        postData
      );
      return data as CustomerHistoryResponseType[] & { hasNextPage?: boolean };
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage.hasNextPage ? allPages.length : undefined;
      return nextPage;
    },
    initialPageParam: 0,
  });
};

export const useGetQuotationById = (quotationId: string) => {
  return useQuery({
    queryKey: ['useGetQuotationById', quotationId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(
        `sales/quotation/${quotationId}`
      );
      return data as QuotationByIdResponse;
    },
  });
};

export const useUpdateQuotation = (options = {}) => {
  return useMutation({
    mutationKey: ['useUpdateQuotation'],
    mutationFn: async (postData: quotaionEditBody) => {
      const { data } = await axiosInstance.put(
        'sales/quotation/update',
        postData
      );
      return data;
    },
    ...options,
  });
};

export const useDeleteQuotation = (options = {}) => {
  return useMutation({
    mutationKey: ['useDeleteQuotation'],
    mutationFn: async (quotationId: string) => {
      const { data } = await axiosInstance.delete(
        `sales/quotation/delete/${quotationId}`
      );
      return data;
    },
    ...options,
  });
};

export const useQuotationPrint = (options = {}) => {
  return useMutation({
    mutationKey: ['usePrintInvoice'],
    mutationFn: async ({ invoiceId }: { invoiceId: string }) => {
      const { data } = await axiosInstance.get(
        `pdf/quotation/html?id=${invoiceId}&templateName=DRAFT`
      );
      return data;
    },
    ...options,
  });
};

export const useQuotePrint = (options = {}) => {
  return useMutation({
    mutationKey: ['usePrintInvoice'],
    mutationFn: async ({ invoiceId }: { invoiceId: string }) => {
      const { data } = await axiosInstance.get(
        `pdf/quotation/html?id=${invoiceId}&templateName=QUOTATION`
      );
      return data;
    },
    ...options,
  });
};
